import { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { Building, BaseComponent } from '../../types/protocols';
import { BaseComponentImpl } from '../../types/implementations';
import { CustomTimelineItem, TimelineEventType } from '../../types/timeline';

/** A timeline event connected to a single component, in display-ready form. */
export interface ConnectedTimelineEvent {
    id: string;
    eventType: TimelineEventType;
    /** One-line text shown in the list. */
    title: string;
    /** Stored date string (YYYY-MM-DD hh:mm:ss). Lexicographically sortable. */
    displayDate: string;
    /** Custom events may be year-only. */
    inexactDate?: boolean;
}

const TITLE_FALLBACK_MAX_LENGTH = 80;

/**
 * Timeline events connected to a component:
 *  - CUSTOM events whose stored `componentKeys` include this component.
 *  - COMPONENT_REPLACED events where this component is the new or the replaced one.
 *
 * Work orders, issues, inspections and projects are intentionally excluded here —
 * those already surface in `ComponentRelatedItems` ("Relaterade aktiviteter").
 *
 * Component-replaced events are derived from the already-loaded building structure;
 * custom events require a one-time read of the building's timeline items.
 */
export function useComponentTimelineEvents(building: Building, componentKey: string) {
    const [events, setEvents] = useState<ConnectedTimelineEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result: ConnectedTimelineEvent[] = [];
            const components = building.model?.structure?.components || {};

            const resolveName = (key: string, data?: BaseComponent): string =>
                data ? new BaseComponentImpl({ ...data, key }).displayName(building) : 'Komponent';

            // 1. COMPONENT_REPLACED events derived from the building structure. A
            // component carrying `replacedFromKey` replaced the (archived) component
            // it points at; the event is dated by that old component's `archivedAt`.
            Object.entries(components).forEach(([key, comp]) => {
                const newComponent = comp as BaseComponent;
                if (!newComponent.replacedFromKey) return;

                // Only events that involve the component being viewed (as new or old).
                if (key !== componentKey && newComponent.replacedFromKey !== componentKey) return;

                const oldComponent = components[newComponent.replacedFromKey] as BaseComponent | undefined;
                if (!oldComponent?.archivedAt) return;

                const newName = resolveName(key, newComponent);
                const oldName = resolveName(newComponent.replacedFromKey, oldComponent);
                result.push({
                    id: `component-replaced-${key}`,
                    eventType: TimelineEventType.COMPONENT_REPLACED,
                    title: `Bytte ut: ${oldName} → ${newName}`,
                    displayDate: oldComponent.archivedAt,
                });
            });

            // 2. CUSTOM events linked to this component.
            const db = getDatabase();
            const snapshot = await get(ref(db, `buildings/${building.key}/timelineItems`));
            if (snapshot.exists()) {
                const items = snapshot.val() as { [id: string]: CustomTimelineItem };
                Object.entries(items).forEach(([id, item]) => {
                    if (!item || !Array.isArray(item.componentKeys)) return;
                    if (!item.componentKeys.includes(componentKey)) return;

                    const trimmedTitle = item.title?.trim();
                    const fallback = (item.description || '').trim().split('\n')[0].slice(0, TITLE_FALLBACK_MAX_LENGTH);
                    result.push({
                        id: `custom-${id}`,
                        eventType: TimelineEventType.CUSTOM,
                        title: trimmedTitle || fallback || 'Egen händelse',
                        displayDate: item.date,
                        inexactDate: item.inexactDate,
                    });
                });
            }

            // Newest first. The stored date format sorts correctly as a string.
            result.sort((a, b) => b.displayDate.localeCompare(a.displayDate));
            setEvents(result);
        } catch (err) {
            console.error('Failed to load connected timeline events:', err);
            setError('Kunde inte ladda anslutna händelser');
        } finally {
            setIsLoading(false);
        }
    }, [building, componentKey]);

    useEffect(() => {
        load();
    }, [load]);

    return { events, isLoading, error, refetch: load };
}
